import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SignUpWizard from '../../components/SignUpWizard/SignUpWizard';
import { apiPrefix } from '../../configs/app.config';
import axios from 'axios';
import { User } from '../../interfaces/user.model';
import Rive from '@rive-app/react-canvas';
import { Col, Container, Row, Button } from 'reactstrap';
import Card from '../../components/ui/Card/Card';
import Alert from '../../components/ui/Alert/Alert';

export default function Wizard() {
  const params = useParams();
  const [user, setUser] = useState<User>();
  const [error, setError] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [step, setStep] = useState<number>(1);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get<User>(
          `${apiPrefix}/auth/${params.token}`
        );
        setUser(response.data);
      } catch (err: any) {
        setError(err?.response?.status || 500);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();

    if (typeof params.step !== 'undefined') {
      setStep(Number(params.step));
    }
  }, [params.token, params.step]);

  const refreshUser = async (token: any) => {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);

      if (!token || token === 'undefined' || token === 'null') {
        setError(400);
        return;
      }

      const response = await fetch(`${apiPrefix}/auth/refresh/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      const status = result.status ?? response.status;

      if (response.ok && status === 200) {
        setMessage(
          result.data ||
            'Check your email for a new link to continue signing up.'
        );
      } else {
        setError(status);
      }
    } catch (error) {
      console.error('Error refreshing user access:', error);
      setError(500);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className={'vh-100'}>
        <Row className={'vh-100'}>
          <Col className='grid-container' style={{ alignItems: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gridColumn: 'span 12',
                maxWidth: '100px',
                margin: '0 auto',
              }}
            >
              <Rive
                src='../../anytime-icon.riv'
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error || message) {
    return (
      <>
        <Helmet>
          <title>Oops!</title>
        </Helmet>
        <Container className={'vh-100'}>
          <Row className={'vh-100'}>
            <Col className='grid-container' style={{ alignItems: 'center' }}>
              <Card className='last-card'>
                <Alert type={message ? 'success' : 'info'} showIcon={true}>
                  {message && <>{message}</>}
                  {error === 403 && (
                    <>
                      The link you are visiting has expired. Your progress has
                      been saved.
                      <Button
                        data-label='Regain Access'
                        className='continue button contained--primary'
                        color='primary'
                        type='button'
                        style={{ marginTop: '1rem' }}
                        onClick={() => refreshUser(params.token)}
                      >
                        Regain Access
                      </Button>
                    </>
                  )}
                  {error === 404 && (
                    <>
                      No matching user found. Please signup again or contact us.
                      <Button
                        data-label='Contact'
                        className='continue button contained--primary'
                        color='primary'
                        type='button'
                        style={{ marginTop: '1rem' }}
                        href='https://anytimebooking.co.uk/contact-us'
                      >
                        Contact
                      </Button>
                    </>
                  )}
                  {error === 410 && (
                    <>
                      Link Expired. Signup Complete. It appears you have already
                      successfully signed up. Please check your email for
                      account access.
                      <br />
                      <br />
                      If you think this is an error, please contact us.
                      <Button
                        data-label='Contact'
                        className='continue button contained--primary'
                        color='primary'
                        type='button'
                        style={{ marginTop: '1rem' }}
                        href='https://anytimebooking.co.uk/contact-us'
                      >
                        Contact
                      </Button>
                    </>
                  )}
                  {error === 400 && (
                    <>Bad request. Token is missing or invalid.</>
                  )}
                  {error === 500 && (
                    <>
                      Something went wrong on our end. Please try again or
                      contact support.
                    </>
                  )}
                  {!message && ![403, 404, 410, 400, 500].includes(error!) && (
                    <>Unexpected error: {error}</>
                  )}
                </Alert>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      {user && (
        <>
          <Helmet>
            <title>{`Hi ${user.firstName} - Finish Signing up`}</title>
          </Helmet>
          <SignUpWizard user={user} step={step} setUser={setUser} />
        </>
      )}
    </>
  );
}
